import { bufferCount, map, Observable, of, share } from "rxjs";

import { useStore } from "@/core/utils/hooks";

import { Store } from "..";
import {
  AppState,
  type AppNotificationType,
  type AppOverlayPanelStateType,
} from "./app_state";

import type { AppActions } from "./app_actions";
import type { AppHooks } from "./app_hooks";
import type { ReactNode } from "react";

export class AppStore extends Store<AppState, AppActions, AppHooks> {
  private static _instance: AppStore;

  static get instance() {
    AppStore._instance = AppStore._instance || new AppStore();
    return this._instance;
  }
  constructor() {
    super(
      new AppState(),
      {
        dialog: (
          method: "open" | "close",
          children?: ReactNode,
          onClose?: () => void,
        ) => {
          this.state.dialog(method, children, onClose);
        },
        rightDrawer: (
          method: "open" | "close",
          size?: number,
          children?: ReactNode,
          onClose?: () => void,
        ) => {
          this.state.rightDrawer(method, size, children, onClose);
        },
        leftDrawer: function (
          method: "open" | "close",
          size?: number,
          children?: ReactNode,
          onClose?: () => void,
        ): void {
          AppStore.instance.state.leftDrawer(method, size, children, onClose);
        },
        isLoading: function (isLoading: boolean): void {
          AppStore.instance.state.isLoading(isLoading);
        },
        notification(code, message, duration, shown) {
          AppStore.instance.state.addNotification({
            code,
            message,
            duration,
            shown,
          });
        },
        clear() {
          AppStore.instance.state.dialog("close");
          AppStore.instance.state.leftDrawer("close");
          AppStore.instance.state.rightDrawer("close");
          AppStore.instance.state.isLoading(false);
        },
      },
      {
        useAppDialog: function (): {
          dialog: AppOverlayPanelStateType | undefined;
          actions: AppActions;
          isConnected: boolean;
        } {
          const { state, actions, isConnected } = useStore(AppStore.instance);
          return {
            actions,
            isConnected,
            dialog: state?.dialog,
          };
        },
        useAppRightDrawer: function (): {
          rightDrawer: AppOverlayPanelStateType | undefined;
          actions: AppActions;
          isConnected: boolean;
        } {
          const { state, actions, isConnected } = useStore(AppStore.instance);
          return {
            actions,
            isConnected,
            rightDrawer: state?.rightDrawer,
          };
        },
        useAppLeftDrawer: function (): {
          leftDrawer: AppOverlayPanelStateType | undefined;
          actions: AppActions;
          isConnected: boolean;
        } {
          const { state, actions, isConnected } = useStore(AppStore.instance);
          return {
            actions,
            isConnected,
            leftDrawer: state?.leftDrawer,
          };
        },
        useAppIsLoading: function (): {
          isLoading: boolean;
          actions: AppActions;
          isConnected: boolean;
        } {
          const { state, actions, isConnected } = useStore(AppStore.instance);
          return {
            actions,
            isConnected,
            isLoading: !!state?.isLoading,
          };
        },
        useAppNotifications: function (): {
          $notifications: Observable<(AppNotificationType | null)[]>;
          actions: AppActions;
          isConnected: boolean;
        } {
          const { state, actions, isConnected } = useStore(AppStore.instance);
          return {
            actions,
            isConnected,
            $notifications:
              state?.$notifier?.$notifications.pipe(
                share(),
                bufferCount(state.$notifier.bufferSize),
                map((news) =>
                  news.reduce<(AppNotificationType | null)[]>(
                    (acc, current) => [current, ...acc],
                    [],
                  ),
                ),
              ) ?? of([]),
          };
        },
      },
    );
  }
}
